import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageCircle, MapPin, Clock, Shield, Users, CheckCircle, Car, Navigation as NavigationIcon, Star, Quote, Plane, Wifi, Anchor, Hotel } from 'lucide-react';
import MailingListSignup from '../components/MailingListSignup';
import HeyTaxiPreview from '../components/HeyTaxiPreview';
import LeaveReviewSection from '../components/LeaveReviewSection';
import SEOHead from '../components/SEOHead';
import { SchemaScript, generateLocalBusinessSchema, generateFAQSchema, generateServiceSchema } from '../utils/seoSchema';

const Home = () => {
  const navigate = useNavigate();
  // Hardcoded testimonials for homepage display
  const testimonials = [
    {
      id: 'static-1',
      rating: 5,
      comment:
        'Mr Smith took amazing care of me. I felt very safe and protected. No need to look any further for your transportation needs. His rates are fair and reasonable. His communication and professionalism is unmatched. Thank you again Mr Smith!',
      attribution: 'Verified Customer (Tripadvisor)',
      verifiedSource: 'Tripadvisor',
      reviewUrl:
        'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1056994579-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
    },
    {
      id: 'static-2',
      rating: 5,
      comment:
        'Steven was absolutely exceptional! Truly the best driver in the Bahamas. He made my entire stay effortless and enjoyable.',
      attribution: 'Roam14619654541 (Tripadvisor)',
      verifiedSource: 'Tripadvisor',
      reviewUrl:
        'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1057921985-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
    },
    {
      id: 'static-3',
      rating: 5,
      comment:
        "Steve gives amazing service and doesn't over charge you. He made sure I was ok when I was sick during my vacation and told me to call him anytime I needed him to take me anywhere. He went above and beyond to make sure I was ok. And help me salvage my vacation. You can't go wrong with him as your driver. If you need a taxi or an island tour he's the guy to get you there.",
      attribution: 'sharon t (Tripadvisor)',
      verifiedSource: 'Tripadvisor',
      reviewUrl:
        'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1057997405-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
    },
  ];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const faqData = [
    {
      question: "Do you provide Nassau airport pickups?",
      answer: "Yes. Taxi-98 provides airport pickup and drop-off service to and from Lynden Pindling International Airport."
    },
    {
      question: "Do you serve Paradise Island?",
      answer: "Yes. We provide rides between Nassau, Paradise Island, hotels, resorts, and other key locations."
    },
    {
      question: "Can I book before I arrive in The Bahamas?",
      answer: "Yes. Advance booking is recommended so your transportation is arranged before arrival."
    },
    {
      question: "Can I contact you directly on WhatsApp?",
      answer: "Yes. You can contact Taxi-98 directly on WhatsApp for bookings and questions."
    },
    {
      question: "Do you provide private transportation?",
      answer: "Yes. We provide private pre-arranged rides for airport transfers, hotel transportation, and general travel needs."
    },
    {
      question: "Can I pre-book a taxi in Nassau before I arrive?",
      answer: "Yes. Taxi-98 accepts advance reservations for airport transfers, hotel and resort transportation, cruise-port transportation, private rides and tours. Pre-booking is recommended when you already know your travel date and pickup time."
    },
    {
      question: "How do I book transportation from Nassau Airport?",
      answer: "You can pre-book Taxi-98 transportation from Lynden Pindling International Airport (LPIA) through our online booking system. Provide your arrival information, destination and contact details so your pickup can be properly scheduled."
    },
    {
      question: "Does Taxi-98 provide transportation from Nassau Airport to Atlantis?",
      answer: "Yes. Taxi-98 provides pre-booked private transportation between Lynden Pindling International Airport (LPIA) and Atlantis on Paradise Island."
    },
    {
      question: "Does Taxi-98 provide transportation from Nassau Airport to Baha Mar?",
      answer: "Yes. Taxi-98 provides pre-booked transportation from LPIA to the Baha Mar resort area, including Grand Hyatt Baha Mar, SLS Baha Mar and Rosewood Baha Mar."
    },
    {
      question: "Does Taxi-98 provide Nassau Cruise Port pickup and drop-off?",
      answer: "Yes. Taxi-98 provides scheduled pickup and drop-off at the Nassau Cruise Port for cruise passengers who need transportation around Nassau, New Providence or Paradise Island."
    },
    {
      question: "Can cruise passengers pre-book transportation in Nassau?",
      answer: "Yes. Cruise passengers can pre-book Taxi-98 for transportation, private island tours, resort or day-pass transportation, Paradise Island visits and scheduled return transportation to the cruise port."
    },
    {
      question: "Does Taxi-98 accept credit cards?",
      answer: "Yes. Taxi-98 accepts major credit cards, including Visa, Mastercard, American Express and Discover."
    },
    {
      question: "Does Taxi-98 accept Apple Pay or Tap-to-Pay?",
      answer: "Yes. Apple Pay / Tap-to-Pay is accepted by Taxi-98 as a convenient payment option."
    },
    {
      question: "Does Taxi-98 accept PayPal?",
      answer: "Yes. Taxi-98 accepts PayPal in addition to major credit cards, Apple Pay / Tap-to-Pay and cash."
    },
    {
      question: "Does Taxi-98 monitor arriving flights?",
      answer: "Yes. When flight information is provided with an airport reservation, Taxi-98 can monitor the flight so we are aware of schedule changes that may affect the planned pickup."
    },
    {
      question: "Does Taxi-98 provide private tours in Nassau?",
      answer: "Yes. Taxi-98 offers relaxing private Nassau tours for visitors who want a more personal way to experience the island. Tours can include historical, cultural, scenic and local stops depending on the selected experience and availability."
    },
    {
      question: "What areas does Taxi-98 serve?",
      answer: "Taxi-98 provides transportation throughout Nassau, New Providence and Paradise Island, including LPIA airport transfers, hotels and resorts, the Nassau Cruise Port, private residences, local transportation and scheduled private rides."
    }
  ];

  const services = [
    {
      icon: <Plane className="w-7 h-7 text-cyan-600" />,
      title: "Nassau Airport Transfers",
      description: "Reliable pickups and drop-offs at Lynden Pindling International Airport (LPIA), with live ride tracking once your driver is assigned.",
      accent: "from-cyan-500/10 to-blue-500/10",
    },
    {
      icon: <MapPin className="w-7 h-7 text-coral-600" />,
      title: "Hotel & Resort Transportation",
      description: "Comfortable transfers to hotels, resorts, and vacation rentals across Nassau, Cable Beach, and Paradise Island.",
      accent: "from-coral-500/10 to-amber-500/10",
    },
    {
      icon: <Users className="w-7 h-7 text-blue-600" />,
      title: "Cruise Port & Downtown Transfers",
      description: "Smooth pickup and drop-off service for cruise passengers at Prince George Dock and around the downtown core.",
      accent: "from-blue-500/10 to-indigo-500/10",
    },
    {
      icon: <Clock className="w-7 h-7 text-emerald-600" />,
      title: "Private Scheduled Rides",
      description: "Pre-arranged transportation for early-morning pickups, business meetings, evening transfers, and important appointments.",
      accent: "from-emerald-500/10 to-teal-500/10",
    },
    {
      icon: <NavigationIcon className="w-7 h-7 text-purple-600" />,
      title: "Tours & Island Rides",
      description: "Discover Nassau and Paradise Island at your own pace with a dependable, knowledgeable local driver. Free Wi-Fi and cold refreshments help make longer rides and tours more comfortable.",
      accent: "from-purple-500/10 to-fuchsia-500/10",
    },
    {
      icon: <Shield className="w-7 h-7 text-amber-500" />,
      title: "Taxi-98 Premier Coming Soon",
      description: "Our upcoming elevated private service for clients who want more comfort, privacy, and a higher level of service.",
      accent: "from-amber-500/10 to-yellow-500/10",
      isPremier: true,
    },
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title="Taxi-98 | Nassau Airport Taxi & Private Transfers in The Bahamas"
        description="Taxi-98 provides reliable Nassau airport taxi service, LPIA pickups, Paradise Island transfers, cruise port transportation, and private rides in The Bahamas. Book your ride today."
        canonical="/"
      />

      {/* Structured Data */}
      <SchemaScript schema={generateLocalBusinessSchema()} />
      <SchemaScript schema={generateServiceSchema()} />
      <SchemaScript schema={generateFAQSchema(faqData)} />

      <div className="bg-white min-h-screen">
        
        {/* Section 1: Hero — Full-bleed brand artwork as background 2026-06-03
            Image fills the entire hero area; dark left-to-right gradient keeps headline readable.
            2026-06-05: Mobile cleanup — tighter type scale, stronger overlay, lighter badge,
                        and extra bottom padding so trust points + CTAs stay clear of the
                        floating WhatsApp button. */}
        <section className="relative min-h-[88vh] sm:min-h-[92vh] flex items-center overflow-hidden bg-gray-950" data-testid="home-hero-section">
          {/* Full-bleed brand artwork */}
          <div className="absolute inset-0 z-0">
            <img
              src="/images/brand/hero-taxi98-bahamas.png"
              alt="Taxi-98 Bahamas — Premium black van transportation at Paradise Island"
              className="w-full h-full object-cover object-center"
              loading="eager"
              data-testid="hero-artwork"
            />
            {/* Mobile: heavier overlay so headline reads cleanly against the busy image */}
            <div className="absolute inset-0 bg-gray-950/85 lg:hidden"></div>
            {/* Desktop: dark gradient from left so headline reads while image stays visible */}
            <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-gray-950 via-gray-950/85 to-gray-950/30"></div>
            {/* Subtle bottom fade into next section */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none z-10"></div>
          </div>

          <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-28 sm:pt-20 sm:pb-20 lg:py-24 w-full">
            <div className="max-w-2xl lg:max-w-2xl">
              {/* Eyebrow badge — smaller and tighter on mobile so it no longer crowds the edges */}
              <span className="inline-block bg-cyan-500/15 backdrop-blur-sm border border-cyan-300/30 text-cyan-200 text-[10px] sm:text-xs font-bold tracking-[0.14em] sm:tracking-[0.18em] uppercase px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-5 sm:mb-6" data-testid="hero-eyebrow">
                Nassau · Bahamas · Est. Taxi-98
              </span>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight sm:leading-[1.08] mb-5 sm:mb-6 tracking-tight drop-shadow-lg" data-testid="hero-headline">
                Reliable Nassau Airport Taxi Service In The Bahamas You Can Book With Confidence
              </h1>

              <p className="text-base sm:text-xl text-gray-100 leading-relaxed mb-7 sm:mb-8 drop-shadow" data-testid="hero-subheadline">
                Book reliable airport transfers, private rides, and scheduled transportation in Nassau with professional service, clear pricing, and dependable pickup.
              </p>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-5 sm:gap-x-6 gap-y-2.5 sm:gap-y-3 text-gray-100 text-sm mb-8 sm:mb-10" data-testid="hero-trust-strip">
                <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-300" /> On-time LPIA pickups</span>
                <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-300" /> Clear, upfront pricing</span>
                <span className="flex items-center gap-2" data-testid="home-payment-compact"><CheckCircle className="w-4 h-4 text-cyan-300" /> Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted</span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4" data-testid="hero-cta-row">
                <button
                  onClick={() => navigate('/book')}
                  data-testid="hero-book-btn"
                  className="group bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-7 py-4 rounded-lg font-bold text-base sm:text-lg hover:from-cyan-300 hover:to-blue-400 transition-all duration-300 transform hover:scale-[1.02] shadow-xl flex items-center justify-center gap-2"
                >
                  <span>Book Your Ride</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  onClick={() => navigate('/fare-calculator')}
                  data-testid="hero-fare-btn"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-7 py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white/20 hover:border-white/50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span>Get Fare Estimate</span>
                </button>

                <a
                  href="https://wa.me/12428107335?text=Hello%20Taxi-98%2C%20I%20would%20like%20to%20book%20a%20ride.%20Please%20assist%20me%20with%20availability."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="bg-green-500 text-white px-7 py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-green-400 transition-all duration-300 shadow-xl flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Call / WhatsApp Now</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Welcome / Trust — overhauled 2026-06-03 */}
        <section className="py-20 sm:py-24 bg-white" data-testid="home-welcome-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* LEFT — heading + copy + trust pills */}
              <div className="lg:col-span-7">
                <span className="inline-block bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full mb-5">
                  Trusted in Nassau
                </span>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-testid="home-welcome-heading">
                  Welcome to Taxi-98
                </h2>

                <div className="space-y-5 text-base sm:text-lg text-gray-700 leading-relaxed">
                  <p>
                    Your ride is often the first part of your Bahamas experience — and sometimes the last impression before you head home. That is why Taxi-98 is built around reliability, clear communication, clean transportation, and professional service from the moment you book.
                  </p>
                  <p>
                    From airport pickups at LPIA to Paradise Island transfers, hotel rides, private transportation, tours, and return trips, customers continue to respond positively to the care and attention we bring to every ride. Our goal is simple: make your transportation feel easy, comfortable, and dependable.
                  </p>
                  <p>
                    Whether you are visiting The Bahamas for the first time, returning for another vacation, traveling for business, or heading home, Taxi-98 is ready to serve with professionalism and pride.
                  </p>
                </div>

                {/* Trust pills — 2026-06-04: added Free Onboard Wi-Fi as 4th pill */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8" data-testid="home-welcome-trust-pills">
                  <div className="flex items-center gap-3 bg-cyan-50/60 border border-cyan-100 rounded-xl px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-cyan-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">On-Time Airport Pickups</p>
                  </div>
                  <div className="flex items-center gap-3 bg-cyan-50/60 border border-cyan-100 rounded-xl px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-4 h-4 text-cyan-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Clear Communication</p>
                  </div>
                  <div className="flex items-center gap-3 bg-cyan-50/60 border border-cyan-100 rounded-xl px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <Car className="w-4 h-4 text-cyan-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Clean, Comfortable Rides</p>
                  </div>
                  <div className="flex items-center gap-3 bg-cyan-50/60 border border-cyan-100 rounded-xl px-4 py-3" data-testid="home-welcome-wifi-pill">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <Wifi className="w-4 h-4 text-cyan-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Free Onboard Wi-Fi</p>
                  </div>
                </div>
              </div>

              {/* RIGHT — customer feedback card (2026-06-04: balanced — sits lower & smaller on desktop) */}
              <div className="lg:col-span-5 lg:mt-20">
                <div
                  data-testid="home-welcome-feedback-card"
                  className="relative bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 text-white rounded-2xl p-6 sm:p-8 shadow-xl overflow-hidden lg:max-w-md lg:ml-auto"
                >
                  {/* Decorative quote mark */}
                  <Quote className="absolute top-5 right-5 w-12 h-12 text-cyan-400/20" aria-hidden="true" />
                  {/* Soft accents */}
                  <div className="absolute -top-20 -left-20 w-56 h-56 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>

                  <span className="relative inline-block bg-cyan-400/15 border border-cyan-300/30 text-cyan-200 text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-4">
                    Customer Feedback
                  </span>

                  <blockquote className="relative">
                    <p className="text-base sm:text-lg text-white leading-relaxed font-medium">
                      &ldquo;Reliable, professional, and easy to communicate with — exactly what you want when arriving in Nassau.&rdquo;
                    </p>
                  </blockquote>

                  <div className="relative mt-6 pt-5 border-t border-white/10">
                    <p className="text-sm text-gray-300">
                      Customer feedback reflects the consistent service Taxi-98 strives to deliver on every ride.
                    </p>
                    <button
                      onClick={() => navigate('/reviews')}
                      data-testid="home-welcome-feedback-cta"
                      className="mt-3 inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-semibold transition-colors"
                    >
                      <span>Read more customer reviews</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Main Services — overhauled 2026-06-03 */}
        <section className="py-20 sm:py-24 bg-gradient-to-b from-gray-50 to-white" data-testid="home-services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section header */}
            <div className="text-center max-w-4xl mx-auto mb-14">
              <span className="inline-block bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full mb-5">
                Our Services
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-5" data-testid="home-services-heading">
                Transportation Services Built Around Your Trip
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                Whether you are arriving at LPIA, heading to your hotel, visiting Paradise Island, planning a tour, or scheduling a private ride, Taxi-98 gives you dependable transportation with clear communication and professional service.
              </p>
              {/* 2026-06-04 — Free Wi-Fi reassurance line under services intro */}
              <p
                data-testid="home-services-wifi-note"
                className="mt-5 inline-flex items-center gap-2 text-sm sm:text-base text-cyan-700 font-medium"
              >
                <Wifi className="w-4 h-4" aria-hidden="true" />
                <span>Enjoy clean, comfortable rides with free onboard Wi-Fi available during your trip.</span>
              </p>
            </div>

            {/* Service cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7" data-testid="home-services-grid">
              {services.map((service, index) => (
                <div
                  key={index}
                  data-testid={`home-service-card-${index}`}
                  className={`group relative bg-white p-7 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border ${
                    service.isPremier
                      ? 'border-amber-200 bg-gradient-to-br from-white to-amber-50/40'
                      : 'border-gray-100'
                  }`}
                >
                  {/* Soft accent flourish on hover */}
                  <div
                    className={`absolute -top-px -right-px w-24 h-24 bg-gradient-to-br ${service.accent} rounded-tr-2xl rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}
                  ></div>

                  {/* Premier badge */}
                  {service.isPremier && (
                    <span className="absolute top-5 right-5 inline-block bg-amber-100 border border-amber-200 text-amber-700 text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 rounded-full">
                      Coming Soon
                    </span>
                  )}

                  <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${
                    service.isPremier ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-100'
                  }`}>
                    {service.icon}
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Customer Praise — real Tripadvisor review excerpt */}
            <div className="mt-14 lg:mt-16 max-w-4xl mx-auto" data-testid="home-services-praise">
              <div className="relative bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 text-white rounded-2xl p-8 sm:p-10 shadow-xl overflow-hidden">
                <Quote className="absolute top-6 right-6 w-16 h-16 text-cyan-400/20" aria-hidden="true" />
                <div className="absolute -top-20 -right-20 w-56 h-56 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>

                <span className="relative inline-block bg-cyan-400/15 border border-cyan-300/30 text-cyan-200 text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 rounded-full mb-5">
                  Customer Praise
                </span>

                <blockquote className="relative">
                  <p className="text-lg sm:text-xl text-white leading-relaxed font-medium">
                    &ldquo;Steven was absolutely exceptional! Truly the best driver in the Bahamas. He made my entire stay effortless and enjoyable.&rdquo;
                  </p>
                </blockquote>

                <div className="relative mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
                      {[0,1,2,3,4].map((i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-gray-300">
                      Roam14619654541 · <span className="text-gray-400">Verified Tripadvisor review</span>
                    </p>
                  </div>
                  <a
                    href="https://www.tripadvisor.in/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-semibold transition-colors"
                    data-testid="home-services-praise-link"
                  >
                    <span>Read all reviews on Tripadvisor</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/*
          Section 3.5: Popular Nassau Transportation Services (2026-09-06)
          Compact internal-linking module that surfaces the four dedicated SEO
          landing pages from the six-stage rollout. No pricing, no booking or
          notification logic touched. Uses descriptive anchor text.
        */}
        <section
          className="py-16 bg-white"
          aria-labelledby="popular-transport-heading"
          data-testid="home-popular-transport-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-4">
                Explore Routes
              </span>
              <h2
                id="popular-transport-heading"
                className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3"
              >
                Popular Nassau Transportation Services
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Pre-book the airport, resort or cruise-port ride that fits your
                trip.
              </p>
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
              data-testid="home-popular-transport-grid"
            >
              <Link
                to="/nassau-airport-transfers"
                data-testid="home-popular-nassau-airport-transfers"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg rounded-2xl p-6 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                  <Plane className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  Nassau Airport Transfers
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Pre-book private transportation from Lynden Pindling
                  International Airport (LPIA) to Nassau, Paradise Island,
                  hotels and resorts.
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-cyan-700 group-hover:text-cyan-800">
                  Nassau Airport Transfers
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>

              <Link
                to="/lpia-to-atlantis"
                data-testid="home-popular-lpia-to-atlantis"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg rounded-2xl p-6 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                  <MapPin className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  LPIA to Atlantis
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Pre-book private transportation from Nassau Airport to
                  Atlantis Paradise Island.
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-cyan-700 group-hover:text-cyan-800">
                  LPIA to Atlantis Transportation
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>

              <Link
                to="/lpia-to-baha-mar"
                data-testid="home-popular-lpia-to-baha-mar"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg rounded-2xl p-6 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                  <Hotel className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  LPIA to Baha Mar
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Private scheduled transportation from LPIA to Baha Mar,
                  including Grand Hyatt, SLS and Rosewood Baha Mar.
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-cyan-700 group-hover:text-cyan-800">
                  LPIA to Baha Mar Transportation
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>

              <Link
                to="/cruise-port-transportation"
                data-testid="home-popular-cruise-port"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg rounded-2xl p-6 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                  <Anchor className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  Nassau Cruise Port Transportation
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Pre-book cruise-port pickup, drop-off, resort transportation
                  and private Nassau transportation.
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-cyan-700 group-hover:text-cyan-800">
                  Nassau Cruise Port Transportation
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Section 4: Why Choose Taxi-98 */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Taxi-98
              </h2>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="space-y-4">
                {[
                  "On-time airport and hotel pickups",
                  "Direct communication by phone or WhatsApp",
                  "Friendly, professional service",
                  "Local knowledge of Nassau and Paradise Island",
                  "Easy pre-booking for peace of mind"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 4.4: Meet Your Driver */}
        <section
          data-testid="home-meet-driver-section"
          className="py-20 bg-white relative overflow-hidden"
        >
          {/* LB-3: SEO — Person/LocalBusiness JSON-LD for the live driver profile.
              Identity-only structured data, no visual change. */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                "@id": "https://www.taxi-98.com/#driver-stephen",
                name: "Stephen Smith",
                jobTitle: "Owner & Driver",
                image:
                  "https://customer-assets.emergentagent.com/job_book-ride-5/artifacts/6c1woock_1000039625.jpg",
                worksFor: {
                  "@type": "LocalBusiness",
                  "@id": "https://www.taxi-98.com/#business",
                  name: "Taxi-98 Bahamas",
                  url: "https://www.taxi-98.com",
                  telephone: "+1-242-810-7335",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: "Nassau",
                    addressRegion: "New Providence",
                    addressCountry: "BS",
                  },
                  areaServed: {
                    "@type": "Place",
                    name: "Nassau, New Providence, Bahamas",
                  },
                },
                url: "https://www.taxi-98.com/#driver-stephen",
                sameAs: [
                  "https://www.tripadvisor.com/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html",
                ],
              }),
            }}
          />
          {/* Soft decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/40 via-white to-blue-50/40 pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <span className="inline-block bg-cyan-50 text-cyan-700 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                Your Driver
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                Meet Your Driver
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              {/* Photo */}
              <div className="lg:col-span-5">
                <div className="relative max-w-sm sm:max-w-md mx-auto">
                  {/* Decorative gradient frame behind the photo */}
                  <div className="absolute -inset-2 sm:-inset-3 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-3xl blur-xl" />
                  <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                    <img
                      data-testid="driver-photo"
                      src="https://customer-assets.emergentagent.com/job_book-ride-5/artifacts/6c1woock_1000039625.jpg"
                      alt="Stephen Smith — Taxi-98 Bahamas driver"
                      className="w-full h-auto aspect-[3/4] object-cover object-top"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                  {/* Subtle name plate at bottom-left of photo */}
                  <div className="absolute -bottom-3 left-3 sm:left-4 bg-white shadow-lg rounded-lg px-4 py-2 border border-gray-100">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Driver / Owner</p>
                    <p className="text-sm font-bold text-gray-900">Stephen Smith</p>
                  </div>
                </div>
              </div>

              {/* Copy + CTA */}
              <div className="lg:col-span-7 text-center lg:text-left">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Stephen Smith
                </h3>
                <p className="text-cyan-700 font-semibold mb-5">
                  Professional Nassau Driver &middot; Owner of Taxi-98
                </p>

                <p className="text-gray-700 leading-relaxed mb-4 text-base sm:text-lg">
                  Stephen has built Taxi-98 around one simple promise: get every passenger to their destination safely, on time, and in a clean, comfortable vehicle.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-base sm:text-lg">
                  From <span className="font-semibold">LPIA airport transfers</span> to private island tours, he provides reliable, courteous, and personalized service for first-time visitors and long-time clients alike.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8 text-base sm:text-lg">
                  When you ride with Taxi-98, you're not getting a random driver — you're getting Stephen, every time.
                </p>

                {/* Trust badges */}
                <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8 max-w-md mx-auto lg:mx-0">
                  <div className="text-center bg-white border border-gray-100 rounded-xl py-3 px-2 shadow-sm">
                    <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-800 leading-tight">Punctual &amp; Courteous</p>
                  </div>
                  <div className="text-center bg-white border border-gray-100 rounded-xl py-3 px-2 shadow-sm">
                    <Car className="w-5 h-5 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-800 leading-tight">Clean Vehicle</p>
                  </div>
                  <div className="text-center bg-white border border-gray-100 rounded-xl py-3 px-2 shadow-sm">
                    <Shield className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-800 leading-tight">Government Rates</p>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                  <button
                    data-testid="meet-driver-book-btn"
                    onClick={() => navigate('/book')}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-7 py-3.5 rounded-lg font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg"
                  >
                    Book Your Ride
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <button
                    data-testid="meet-driver-fare-btn"
                    onClick={() => navigate('/fare-calculator')}
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-7 py-3.5 rounded-lg font-bold text-base sm:text-lg transition-all duration-300 border-2 border-gray-200 hover:border-cyan-500 shadow-sm"
                  >
                    Get Your Fare Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Section 4.5: Customer Testimonials */}
        <section
          className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50"
          data-testid="home-testimonials-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                What Our Customers Say
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {testimonials.map((review) => (
                <div
                  key={review.id}
                  data-testid={`home-testimonial-card-${review.id}`}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 flex flex-col"
                >
                  {/* Quote Icon */}
                  <Quote className="w-8 h-8 text-cyan-500 mb-4" />

                  {/* Rating */}
                  <div
                    className="flex items-center mb-4"
                    aria-label={`${review.rating} out of 5 stars`}
                  >
                    {renderStars(review.rating)}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-700 leading-relaxed">
                    "{review.comment}"
                  </p>

                  {/* Verified attribution + source link */}
                  {review.verifiedSource && (
                    <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col gap-2">
                      {review.attribution && (
                        <p className="text-sm font-semibold text-gray-900">
                          — {review.attribution}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          data-testid={`home-testimonial-verified-badge-${review.id}`}
                          className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Verified on {review.verifiedSource}
                        </span>
                        {review.reviewUrl && (
                          <a
                            data-testid={`home-testimonial-view-link-${review.id}`}
                            href={review.reviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center"
                          >
                            View on {review.verifiedSource}
                            <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Review CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button
                data-testid="home-read-more-reviews-btn"
                onClick={() => navigate('/reviews')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
              >
                <span>Read More Reviews</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                data-testid="home-google-reviews-btn"
                href="https://goo.gl/maps/ZLgKpoDGJR3ahu1y6?g_st=ac"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Read our Google reviews (opens in new tab)"
                className="bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2 border-2 border-black/10"
              >
                <Star className="w-5 h-5 fill-black text-black" />
                <span>Read Our Google Reviews</span>
              </a>
            </div>
          </div>
        </section>

        {/* Section 4.6: Leave a Review — growth-mode conversion section (2026-06-26) */}
        <LeaveReviewSection />

        {/* Section 4.7: Taxi-98 Premier Coming Soon — premium teaser with brand monogram (no "VIP" wording) */}
        <section
          data-testid="home-premier-section"
          className="relative py-20 sm:py-28 bg-gradient-to-br from-black via-gray-950 to-black overflow-hidden"
        >
          {/* Subtle gold-tinted accents */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/8 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-400/8 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              {/* Brand monogram */}
              <div className="flex justify-center mb-8">
                <img
                  src="/images/brand/premier-monogram.png"
                  alt="Taxi-98 Premier — Nassau, The Bahamas"
                  className="w-48 sm:w-56 lg:w-64 h-auto"
                  loading="lazy"
                  data-testid="premier-monogram"
                />
              </div>

              {/* Eyebrow */}
              <span className="inline-block bg-amber-400/10 border border-amber-300/30 text-amber-200 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-6" data-testid="premier-eyebrow">
                Coming Soon
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" data-testid="premier-headline">
                Taxi-98 Premier Coming Soon
              </h2>

              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto">
                Taxi-98 Premier is our upcoming elevated private transportation service for clients who want more comfort, privacy, and a higher level of service in Nassau. Designed for airport transfers, private rides, special occasions, business travel, and premium guest experiences.
              </p>

              <p className="text-base text-gray-400 italic mb-10">
                More details coming soon.
              </p>

              {/* Premium pillars */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                  <Shield className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-white">Private &amp; Discreet</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                  <Car className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-white">Elevated Comfort</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-amber-300/15 rounded-xl py-5 px-4">
                  <Clock className="w-6 h-6 text-amber-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-white">By Appointment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Service Areas */}
        <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Areas We Serve
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Taxi-98 serves customers traveling throughout Nassau, Paradise Island, Lynden Pindling International Airport (LPIA), the cruise port, major hotels, resorts, and nearby destinations in New Providence.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Booking */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Book Your Ride in Advance
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Avoid the stress of waiting for transportation after your flight or cruise arrival. Pre-book your ride with Taxi-98 and travel with more confidence. We make it easy to arrange airport pickups, hotel transfers, and private rides before you arrive.
              </p>
              <button
                onClick={() => navigate('/book')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-4 rounded-lg font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
              >
                <span>Book Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* Section 7: Contact Summary */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Contact Taxi-98
              </h2>
              <p className="text-lg text-gray-600">
                Get in touch to book your ride or ask questions
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Phone */}
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-cyan-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Phone Lines</p>
                      <a href="tel:+12428107335" className="text-base font-semibold text-gray-900 hover:text-cyan-600 block">
                        1-242-810-7335 <span className="text-xs text-gray-500">(Business)</span>
                      </a>
                      <a href="tel:+12428100098" className="text-base font-semibold text-gray-900 hover:text-cyan-600 block mt-1">
                        1-242-810-0098 <span className="text-xs text-gray-500">(Management)</span>
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600 mb-1">WhatsApp</p>
                      <a href="https://wa.me/12428107335" target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-gray-900 hover:text-green-600 block">
                        1-242-810-7335 <span className="text-xs text-gray-500">(Business)</span>
                      </a>
                      <a href="https://wa.me/12428100098" target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-gray-900 hover:text-green-600 block mt-1">
                        1-242-810-0098 <span className="text-xs text-gray-500">(Management)</span>
                      </a>
                    </div>
                  </div>

                  {/* Owner Email */}
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Owner / General</p>
                      <a href="mailto:stephen@taxi-98.com" className="text-base font-semibold text-gray-900 hover:text-blue-600 break-all">
                        stephen@taxi-98.com
                      </a>
                    </div>
                  </div>

                  {/* Bookings Email */}
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-coral-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Bookings</p>
                      <a href="mailto:bookings@taxi-98.com" className="text-base font-semibold text-gray-900 hover:text-coral-600 break-all">
                        bookings@taxi-98.com
                      </a>
                    </div>
                  </div>

                  {/* Website */}
                  <div className="flex items-start space-x-3 md:col-span-2">
                    <svg className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Website</p>
                      <a href="https://www.taxi-98.com" className="text-base font-semibold text-gray-900 hover:text-purple-600">
                        www.taxi-98.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Review Links Section */}
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg border border-cyan-200 mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="w-5 h-5 mr-2 text-yellow-500" />
                    Leave Us a Review
                  </h3>
                  <div className="space-y-3">
                    {/* Google Review Link - NEW */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">⭐ Review Us on Google</p>
                        <p className="text-sm text-gray-600 mb-2">
                          Share your experience on Google and help others discover our service.
                        </p>
                        <a 
                          href="https://g.page/r/CRsSkN508n5DECA/review"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg transition-colors duration-300"
                        >
                          <Star className="w-4 h-4 mr-2 fill-yellow-300 text-yellow-300" />
                          Write a Google Review
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                      </div>
                    </div>

                    {/* Taxi-98 Review Link */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-cyan-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Leave a Review on Taxi-98</p>
                        <p className="text-sm text-gray-600 mb-2">
                          To leave a review on Taxi-98, please use your booking number.
                        </p>
                        <a 
                          href="/reviews" 
                          className="text-cyan-600 hover:text-cyan-700 font-medium text-sm inline-flex items-center"
                        >
                          View Reviews & Submit
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>

                    {/* Tripadvisor Review Link */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Leave a Review on Tripadvisor</p>
                        <p className="text-sm text-gray-600 mb-2">
                          Leave a public review on our Tripadvisor page.
                        </p>
                        <a 
                          href="https://www.tripadvisor.com/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 font-medium text-sm inline-flex items-center"
                        >
                          Write a Tripadvisor Review
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Link to Full Contact Page */}
                <div className="text-center pt-6 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="inline-flex items-center space-x-2 text-cyan-600 hover:text-cyan-700 font-semibold"
                  >
                    <span>View Full Contact Page</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: FAQ */}
        <section id="faq" className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hey Taxi! homepage preview — 2026-06-10
            Lightweight teaser linking to the new blog. The blog list itself is
            still the source of truth; this section uses a static client-side
            preview so the homepage doesn't fire an extra API call on every
            visit. The "Read Hey Taxi!" CTA is the primary entry point. */}
        <HeyTaxiPreview navigate={navigate} />

        {/* Mailing List */}
        <MailingListSignup />
      </div>
    </>
  );
};

export default Home;
